import { useEffect, useRef, useState } from 'react'

export type SearchFn<T> = (query: string, signal: AbortSignal) => Promise<T[]>

/**
 * Runs `search` after the query stops changing, cancelling any request a newer query
 * replaces. An empty query clears the results without searching.
 * @param search - Fetches results for a query; the latest function passed is always used
 * @param delay - Milliseconds to wait after the last change before searching
 * @returns The query, a setter for it, the results, whether a search is in flight or
 * failed, and a function to search the current query again
 */
export const useDebouncedSearch = <T>(search: SearchFn<T>, delay = 300) => {
  const [query, setQueryState] = useState('')
  const [results, setResults] = useState<T[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasError, setHasError] = useState(false)
  const searchRef = useRef(search)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  const abortControllerRef = useRef<AbortController | null>(null)

  searchRef.current = search

  const cancel = () => {
    clearTimeout(timeoutRef.current)
    abortControllerRef.current?.abort()
  }

  useEffect(() => cancel, [])

  const runSearch = async (query: string) => {
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    let nextResults: T[] = []
    let failed = false
    try {
      nextResults = await searchRef.current(query, abortController.signal)
    } catch {
      failed = true
    }

    // Callers' search functions may ignore the signal, so a superseded search can still resolve
    if (abortController.signal.aborted) {
      return
    }
    setResults(nextResults)
    setHasError(failed)
    setIsSearching(false)
  }

  const setQuery = (query: string) => {
    setQueryState(query)
    setHasError(false)
    cancel()

    if (query.trim() === '') {
      setResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    timeoutRef.current = setTimeout(() => runSearch(query), delay)
  }

  const retry = () => setQuery(query)

  return { query, setQuery, results, isSearching, hasError, retry }
}
